import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  CurrencyDollarIcon,
  UserGroupIcon,
  LinkIcon,
  ChartBarIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/auth';
import api from '../services/api';
import { StatCard } from '../components/ui/StatCard';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import toast from 'react-hot-toast';

interface AffiliateStats {
  totalClicks: number;
  totalRegistrations: number;
  totalDeposits: number;
  totalCommissions: number;
  pendingCommissions: number;
  conversionRate: number;
}

interface BalanceInfo {
  balance: number;
  pendingBalance: number;
  totalPaid: number;
}

export default function Dashboard() {
  const { affiliate } = useAuthStore();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['affiliate-stats', affiliate?.id],
    queryFn: async () => {
      const response = await api.get(`/affiliates/${affiliate?.id}/stats`);
      return response.data.data as AffiliateStats;
    },
    enabled: !!affiliate?.id,
  });

  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ['affiliate-balance', affiliate?.id],
    queryFn: async () => {
      const response = await api.get(`/payouts/balance/${affiliate?.id}`);
      return response.data.data as BalanceInfo;
    },
    enabled: !!affiliate?.id,
  });

  const isLoading = statsLoading || balanceLoading;

  const referralUrl = affiliate?.referralCode
    ? `https://app.worldreach.io/register?ref=${affiliate.referralCode}`
    : '';

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="h-10 w-48 bg-[var(--bg-tertiary)] rounded-xl animate-pulse" />
        <div className="h-40 bg-[var(--bg-tertiary)] rounded-2xl animate-pulse" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-[var(--bg-tertiary)] rounded-2xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-display-lg text-gradient">Dashboard</h1>
        <p className="mt-1 text-[var(--text-secondary)]">
          Welcome back, {affiliate?.name}! Here's an overview of your performance.
        </p>
      </motion.div>

      {/* Referral Link Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card variant="glass" className="overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600/20 to-accent-500/20" />
          <CardBody className="relative">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h2 className="text-heading text-[var(--text-primary)]">Your Referral Link</h2>
                <p className="mt-1 text-[var(--text-secondary)]">
                  Share this link to earn commissions on every player you refer.
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <span className="text-[var(--text-muted)]">Referral Code:</span>
                  <Badge variant="purple" size="lg">
                    <span className="font-mono font-bold">{affiliate?.referralCode}</span>
                  </Badge>
                </div>
              </div>

              <div className="flex-1 lg:max-w-xl">
                <div className="flex items-center gap-3 bg-[var(--bg-tertiary)] rounded-xl p-4 border border-[var(--border-color)]">
                  <code className="flex-1 text-sm font-mono text-[var(--text-primary)] truncate">
                    {referralUrl || 'Loading...'}
                  </code>
                  {referralUrl && (
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<ClipboardDocumentIcon className="w-4 h-4" />}
                      onClick={() => copyToClipboard(referralUrl)}
                    >
                      Copy
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <StatCard
            title="Total Clicks"
            value={stats?.totalClicks?.toLocaleString() || '0'}
            icon={<ChartBarIcon className="w-6 h-6" />}
            gradient="purple"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <StatCard
            title="Registrations"
            value={stats?.totalRegistrations?.toLocaleString() || '0'}
            icon={<UserGroupIcon className="w-6 h-6" />}
            gradient="cyan"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <StatCard
            title="Active Links"
            value={affiliate?.smartLinksCount?.toString() || '0'}
            icon={<LinkIcon className="w-6 h-6" />}
            gradient="green"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <StatCard
            title="Conversion Rate"
            value={`${(stats?.conversionRate || 0).toFixed(1)}%`}
            icon={<ChartBarIcon className="w-6 h-6" />}
            gradient="orange"
          />
        </motion.div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--text-secondary)]">Available Balance</p>
                  <p className="mt-2 text-3xl font-bold text-success-400">
                    {formatCurrency(balance?.balance || 0)}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-success-500/20">
                  <CurrencyDollarIcon className="h-6 w-6 text-success-400" />
                </div>
              </div>
              <p className="mt-3 text-sm text-[var(--text-muted)]">Ready for withdrawal</p>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
        >
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--text-secondary)]">Pending Commissions</p>
                  <p className="mt-2 text-3xl font-bold text-warning-400">
                    {formatCurrency(balance?.pendingBalance || stats?.pendingCommissions || 0)}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-warning-500/20">
                  <CurrencyDollarIcon className="h-6 w-6 text-warning-400" />
                </div>
              </div>
              <p className="mt-3 text-sm text-[var(--text-muted)]">Awaiting approval</p>
            </CardBody>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card>
            <CardBody>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--text-secondary)]">Total Paid</p>
                  <p className="mt-2 text-3xl font-bold text-primary-400">
                    {formatCurrency(balance?.totalPaid || stats?.totalCommissions || 0)}
                  </p>
                </div>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/20">
                  <CurrencyDollarIcon className="h-6 w-6 text-primary-400" />
                </div>
              </div>
              <p className="mt-3 text-sm text-[var(--text-muted)]">All-time earnings</p>
            </CardBody>
          </Card>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
      >
        <Card variant="gradient">
          <CardHeader>
            <h2 className="text-heading text-[var(--text-primary)]">Quick Actions</h2>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <a
                  href="/links"
                  className="flex items-center p-4 border border-[var(--border-color)] rounded-xl hover:border-primary-500/50 hover:bg-[var(--bg-card)] transition-all duration-200 group"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500/20 group-hover:bg-primary-500/30 transition-colors">
                    <LinkIcon className="h-6 w-6 text-primary-400" />
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold text-[var(--text-primary)]">Create Smart Link</p>
                    <p className="text-sm text-[var(--text-muted)]">Generate tracked links</p>
                  </div>
                </a>
              </motion.div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <a
                  href="/commissions"
                  className="flex items-center p-4 border border-[var(--border-color)] rounded-xl hover:border-accent-500/50 hover:bg-[var(--bg-card)] transition-all duration-200 group"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-500/20 group-hover:bg-accent-500/30 transition-colors">
                    <CurrencyDollarIcon className="h-6 w-6 text-accent-400" />
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold text-[var(--text-primary)]">View Commissions</p>
                    <p className="text-sm text-[var(--text-muted)]">See your earnings</p>
                  </div>
                </a>
              </motion.div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <a
                  href="/profile"
                  className="flex items-center p-4 border border-[var(--border-color)] rounded-xl hover:border-success-500/50 hover:bg-[var(--bg-card)] transition-all duration-200 group"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-success-500/20 group-hover:bg-success-500/30 transition-colors">
                    <UserGroupIcon className="h-6 w-6 text-success-400" />
                  </div>
                  <div className="ml-4">
                    <p className="font-semibold text-[var(--text-primary)]">Update Profile</p>
                    <p className="text-sm text-[var(--text-muted)]">Manage your account</p>
                  </div>
                </a>
              </motion.div>
            </div>
          </CardBody>
        </Card>
      </motion.div>
    </div>
  );
}
